import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Copy, KeyRound, Loader2, Rocket } from 'lucide-react';

import {
  clientsAPI,
  driversAPI,
  ordersAPI,
  trucksAPI,
} from '../lib/api';
import { fetchAllPages } from '../lib/fetchAllPages';
import { messageErreur } from '../lib/erreurs';
import { useAuth } from '../context/AuthContext';
import { PageContainer } from '../components/ui/page-container';
import { PageHeader } from '../components/ui/page-header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { SearchableSelect } from '../components/ui/searchable-select';
import { EmptyState } from '../components/ui/empty-state';
import { FormActions, FormStep } from '../components/ui/page-layouts';
import { toast } from '../components/ui/sonner';
import {
  buildOnboardingSteps,
  readOnboardingProgress,
  writeOnboardingProgress,
} from '../components/onboarding';

/**
 * Le premier jour, en quatre saisies.
 *
 * L'écran ne fait rien que les pages Camions, Chauffeurs, Clients et
 * Commandes ne sachent déjà faire : il appelle les mêmes API. Ce qu'il ajoute
 * est un ordre et une fin — un chauffeur muni de son code d'accès — pour que
 * la première demi-heure produise un résultat au lieu d'un parc à moitié
 * saisi.
 */

// Une citerne courante en Afrique de l'Ouest. La valeur est modifiable et
// visible : elle existe parce que l'API refuse un camion sans capacité, et
// qu'ouvrir le parcours sur une question de litres ferait fuir quelqu'un qui
// venait juste taper une plaque.
const DEFAULT_CAPACITY_LITERS = '40000';

const EMPTY_FORM = {
  matricule: '',
  capacity_liters: DEFAULT_CAPACITY_LITERS,
  driver_name: '',
  driver_phone: '',
  client_name: '',
  client_phone: '',
  client_id: '',
  quantity_liters: '',
  unit_price: '',
  delivery_location: '',
};

const trimmed = (value) => String(value || '').trim();

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { hasModule } = useAuth();

  const steps = useMemo(() => buildOnboardingSteps(hasModule), [hasModule]);
  const stepCount = steps.length;

  const [stepIndex, setStepIndex] = useState(() =>
    Math.min(readOnboardingProgress().stepIndex, steps.length),
  );
  const [created, setCreated] = useState(() => readOnboardingProgress().created);
  // Le serveur ne renvoie ce code qu'une fois. Il reste en mémoire de l'écran
  // et n'est jamais persisté.
  const [accessCode, setAccessCode] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [clients, setClients] = useState([]);
  const clientOptions = useMemo(
    () =>
      clients.map((client) => ({
        value: client.id,
        label: client.name || 'Client sans nom',
        description: client.city || undefined,
        keywords: [client.contact_name, client.phone, client.tax_id],
      })),
    [clients],
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const step = stepIndex < stepCount ? steps[stepIndex] : null;
  const stepId = step?.id;

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const advance = useCallback(
    (patch) => {
      const nextCreated = { ...created, ...patch };
      const nextIndex = stepIndex + 1;
      setCreated(nextCreated);
      setStepIndex(nextIndex);
      setError(null);
      writeOnboardingProgress({
        stepIndex: nextIndex,
        created: nextCreated,
        done: nextIndex >= stepCount,
      });
    },
    [created, stepCount, stepIndex],
  );

  // La commande a besoin d'un client existant, y compris quand l'étape client
  // a été passée parce que la société en avait déjà.
  useEffect(() => {
    if (stepId !== 'order') return undefined;
    let cancelled = false;
    fetchAllPages((page) => clientsAPI.getAllPaged(page))
      .then((result) => {
        if (!cancelled) setClients(result?.items || []);
      })
      .catch(() => {
        if (!cancelled) setClients([]);
      });
    return () => {
      cancelled = true;
    };
  }, [stepId]);

  const createdClientId = created.client;
  const firstClientId = clients[0]?.id;
  useEffect(() => {
    if (stepId !== 'order') return;
    const fallback = createdClientId || firstClientId || '';
    if (!fallback) return;
    setForm((prev) => (prev.client_id ? prev : { ...prev, client_id: fallback }));
  }, [createdClientId, firstClientId, stepId]);

  const submitters = {
    truck: async () => {
      const truck = await trucksAPI.create({
        matricule: trimmed(form.matricule),
        capacity_liters: Number(form.capacity_liters),
        ownership_type: 'PROPRE',
      });
      return { truck: truck?.id };
    },
    driver: async () => {
      const driver = await driversAPI.create({
        full_name: trimmed(form.driver_name),
        phone: trimmed(form.driver_phone),
        active: true,
        assigned_truck_id: created.truck || null,
      });
      if (driver?.access_code_once) {
        setAccessCode({
          code: driver.access_code_once,
          name: driver.full_name || trimmed(form.driver_name),
        });
      }
      return { driver: driver?.id };
    },
    client: async () => {
      const client = await clientsAPI.create({
        name: trimmed(form.client_name),
        type: 'THIRD_PARTY',
        phone: trimmed(form.client_phone),
      });
      return { client: client?.id };
    },
    order: async () => {
      const quantity = Number(form.quantity_liters);
      const unitPrice = Number(form.unit_price || 0);
      const order = await ordersAPI.create({
        client_id: form.client_id,
        type: 'SALE',
        status: 'DRAFT',
        quantity_liters: quantity,
        unit_price: unitPrice || undefined,
        total_amount: quantity * unitPrice,
        delivery_location: trimmed(form.delivery_location) || undefined,
      });
      return { order: order?.id };
    },
  };

  const isStepReady = () => {
    if (stepId === 'truck') {
      return Boolean(trimmed(form.matricule)) && Number(form.capacity_liters) > 0;
    }
    if (stepId === 'driver') {
      return Boolean(trimmed(form.driver_name)) && Boolean(trimmed(form.driver_phone));
    }
    if (stepId === 'client') {
      return Boolean(trimmed(form.client_name)) && Boolean(trimmed(form.client_phone));
    }
    if (stepId === 'order') {
      return Boolean(form.client_id) && Number(form.quantity_liters) > 0;
    }
    return false;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stepId || isSaving || !isStepReady()) return;
    setIsSaving(true);
    setError(null);
    try {
      advance(await submitters[stepId]());
    } catch (err) {
      // Le message du serveur porte la règle métier (plaque déjà connue,
      // téléphone déjà pris) : le remplacer par « une erreur est survenue »
      // laisserait l'utilisateur sans rien à corriger.
      setError(messageErreur(err, 'Création impossible. Vérifiez les informations saisies.'));
    } finally {
      setIsSaving(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(accessCode?.code || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      toast.error('Copie impossible : notez le code à la main.');
    }
  };

  if (stepCount === 0) {
    return (
      <PageContainer width="narrow" className="space-y-5">
        <PageHeader eyebrow="Démarrage" title="Démarrer en 10 minutes" />
        <EmptyState
          icon={Rocket}
          title="Ce parcours ne concerne pas votre licence"
          description="Il crée un camion, un chauffeur, un client et une commande. Aucun de ces accès n’est activé pour votre société."
          actionLabel="Aller à l’accueil"
          onAction={() => navigate('/')}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer width="narrow" className="space-y-5" data-testid="onboarding-page">
      <PageHeader
        eyebrow="Démarrage"
        title="Démarrer en 10 minutes"
        description={
          hasModule('drivers')
            ? 'Quelques saisies, et votre premier chauffeur peut se connecter depuis son téléphone.'
            : 'Quelques saisies pour créer le strict minimum et voir l’application tourner sur vos données.'
        }
        meta={
          step ? (
            <span>
              Étape {stepIndex + 1} sur {stepCount} — {step.title}
            </span>
          ) : (
            <span>Terminé</span>
          )
        }
      />

      <div className="flex gap-1.5" aria-hidden="true">
        {steps.map((item, index) => (
          <span
            key={item.id}
            className={
              index < stepIndex
                ? 'h-1 flex-1 rounded-full bg-success'
                : index === stepIndex
                  ? 'h-1 flex-1 rounded-full bg-flame'
                  : 'h-1 flex-1 rounded-full bg-muted'
            }
          />
        ))}
      </div>

      {/* Le code arrive au milieu du parcours, pas à la fin : c'est le premier
          résultat tangible et il doit précéder le reste de la saisie. */}
      {accessCode && (
        <Card className="card-base border-flame/40">
          <CardContent className="space-y-3 p-4">
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-flame" aria-hidden="true" />
              <p className="text-sm font-semibold text-foreground">
                Code d’accès de {accessCode.name}
              </p>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 p-4">
              <code className="flex-1 text-center font-mono text-2xl font-bold tracking-widest text-flame">
                {accessCode.code}
              </code>
              <Button type="button" variant="outline" size="sm" onClick={copyCode}>
                {copied ? (
                  <Check className="h-4 w-4 text-success" aria-hidden="true" />
                ) : (
                  <Copy className="h-4 w-4" aria-hidden="true" />
                )}
                <span className="sr-only">Copier le code</span>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Donnez-le à votre chauffeur : il ouvre l’application mobile avec, sans
              mot de passe. Il ne sera plus affiché en clair — vous pourrez le
              régénérer depuis la fiche du chauffeur.
            </p>
          </CardContent>
        </Card>
      )}

      {step ? (
        <Card className="card-base">
          <CardContent className="pt-4">
            {error && (
              <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <FormStep
                step={String(stepIndex + 1)}
                title={step.title}
                description={step.description}
              >
                {stepId === 'truck' && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label htmlFor="onboarding-matricule">Plaque d’immatriculation</Label>
                      <Input
                        id="onboarding-matricule"
                        autoFocus
                        value={form.matricule}
                        onChange={(e) => setField('matricule', e.target.value.toUpperCase())}
                        placeholder="AA-123-BB"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="onboarding-capacity">Capacité (litres)</Label>
                      <Input
                        id="onboarding-capacity"
                        type="number"
                        min="1"
                        value={form.capacity_liters}
                        onChange={(e) => setField('capacity_liters', e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {stepId === 'driver' && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label htmlFor="onboarding-driver-name">Nom du chauffeur</Label>
                      <Input
                        id="onboarding-driver-name"
                        autoFocus
                        value={form.driver_name}
                        onChange={(e) => setField('driver_name', e.target.value)}
                        placeholder="Amadou Diallo"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="onboarding-driver-phone">Téléphone</Label>
                      <Input
                        id="onboarding-driver-phone"
                        type="tel"
                        value={form.driver_phone}
                        onChange={(e) => setField('driver_phone', e.target.value)}
                        placeholder="+221 77 000 00 00"
                      />
                    </div>
                  </div>
                )}

                {stepId === 'client' && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label htmlFor="onboarding-client-name">Nom du client</Label>
                      <Input
                        id="onboarding-client-name"
                        autoFocus
                        value={form.client_name}
                        onChange={(e) => setField('client_name', e.target.value)}
                        placeholder="Station Kaolack"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="onboarding-client-phone">Téléphone</Label>
                      <Input
                        id="onboarding-client-phone"
                        type="tel"
                        value={form.client_phone}
                        onChange={(e) => setField('client_phone', e.target.value)}
                        placeholder="+221 33 000 00 00"
                      />
                    </div>
                  </div>
                )}

                {stepId === 'order' && (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label>Client</Label>
                      <SearchableSelect
                        value={form.client_id || ''}
                        onValueChange={(value) => setField('client_id', value)}
                        options={clientOptions}
                        placeholder="Sélectionner un client"
                        searchPlaceholder="Rechercher un client…"
                        emptyMessage="Aucun client trouvé"
                      />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <Label htmlFor="onboarding-quantity">Volume commandé (litres)</Label>
                        <Input
                          id="onboarding-quantity"
                          type="number"
                          min="1"
                          value={form.quantity_liters}
                          onChange={(e) => setField('quantity_liters', e.target.value)}
                          placeholder="30000"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="onboarding-price">Prix au litre (facultatif)</Label>
                        <Input
                          id="onboarding-price"
                          type="number"
                          min="0"
                          value={form.unit_price}
                          onChange={(e) => setField('unit_price', e.target.value)}
                          placeholder="Prix au litre"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="onboarding-location">Lieu de livraison (facultatif)</Label>
                      <Input
                        id="onboarding-location"
                        value={form.delivery_location}
                        onChange={(e) => setField('delivery_location', e.target.value)}
                        placeholder="Kaolack"
                      />
                    </div>
                  </div>
                )}
              </FormStep>

              <FormActions
                hint={
                  <Aide titre="Après l’enregistrement">
                    Vous pourrez tout modifier ensuite depuis les écrans habituels.
                  </Aide>
                }
              >
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => advance({})}
                  disabled={isSaving}
                >
                  Passer cette étape
                </Button>
                <Button type="submit" disabled={isSaving || !isStepReady()}>
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <Check className="h-4 w-4" aria-hidden="true" />
                  )}
                  {stepIndex + 1 === stepCount ? 'Terminer' : 'Enregistrer et continuer'}
                </Button>
              </FormActions>
            </form>
          </CardContent>
        </Card>
      ) : (
        <Card className="card-base">
          <CardContent className="space-y-4 p-5">
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-success" aria-hidden="true" />
              <h2>C’est en place</h2>
              <Aide titre="C’est en place">
                {accessCode
                  ? 'Votre chauffeur peut se connecter dès maintenant avec le code ci-dessus. Le reste du parc s’ajoute au fur et à mesure.'
                  : 'Le nécessaire est créé. Le reste du parc s’ajoute au fur et à mesure, écran par écran.'}
              </Aide>
            </div>
            <div className="flex flex-wrap gap-2">
              {created.truck && hasModule('trucks') && (
                <Button variant="outline" onClick={() => navigate('/trucks')}>
                  Voir mes camions
                </Button>
              )}
              {created.driver && hasModule('drivers') && (
                <Button variant="outline" onClick={() => navigate('/drivers')}>
                  Voir mes chauffeurs
                </Button>
              )}
              {created.order && hasModule('orders') && (
                <Button variant="outline" onClick={() => navigate('/orders')}>
                  Voir la commande
                </Button>
              )}
              {hasModule('trips') && (
                <Button onClick={() => navigate('/trips/new')}>
                  Lancer un premier trajet
                </Button>
              )}
              <Button variant="ghost" onClick={() => navigate('/')}>
                Aller à l’accueil
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
};

export default OnboardingPage;
